import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Alert, Typography, ToggleButton, ToggleButtonGroup, Box
} from '@mui/material';
import api from '../../services/api';

/**
 * Item 82 Phase 2 — add a portfolio (entity) under this login. Two paths:
 *   Create — a fresh set of books (empty NAV universe, own accounts).
 *   Adopt  — an EXISTING standalone Kodahfy account (the SMSF-stopgap
 *   users): prove you own it with its email+password and it becomes an
 *   entity here. Nothing re-imports; its old login stops working.
 */

const ENTITY_TYPES = [
  { value: 'personal', label: 'Personal' },
  { value: 'smsf', label: 'SMSF / Super fund' },
  { value: 'trust', label: 'Trust' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other' }
];

const EntityDialog = ({ open, onClose, onCreated }) => {
  const [mode, setMode] = useState('create');
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState('smsf');
  const [adoptEmail, setAdoptEmail] = useState('');
  const [adoptPassword, setAdoptPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setName(''); setEntityType('smsf'); setAdoptEmail(''); setAdoptPassword('');
    setError(null); setMode('create');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = mode === 'create'
        ? { name: name.trim(), entity_type: entityType }
        : { email: adoptEmail.trim(), password: adoptPassword, name: name.trim() || undefined, entity_type: entityType };
      const res = mode === 'create'
        ? await api.post('/entities', payload)
        : await api.post('/entities/adopt', payload);
      const entity = res.data?.data?.entity;
      reset();
      onCreated?.(entity);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add portfolio');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = mode === 'create'
    ? name.trim().length > 0
    : adoptEmail.trim().length > 0 && adoptPassword.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add a portfolio</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <ToggleButtonGroup
          size="small" exclusive value={mode}
          onChange={(e, v) => { if (v) { setMode(v); setError(null); } }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
        >
          <ToggleButton value="create">Start fresh</ToggleButton>
          <ToggleButton value="adopt">Link an existing account</ToggleButton>
        </ToggleButtonGroup>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {mode === 'adopt' && (
          <>
            <Typography variant="body2" color="text.secondary">
              Already running this portfolio in a separate Kodahfy account?
              Enter its login and it moves under this one — all data intact,
              switchable from the menu. Its old login stops working.
            </Typography>
            <TextField label="That account's email" size="small" type="email" fullWidth
              value={adoptEmail} onChange={(e) => setAdoptEmail(e.target.value)} />
            <TextField label="That account's password" size="small" type="password" fullWidth
              value={adoptPassword} onChange={(e) => setAdoptPassword(e.target.value)} />
          </>
        )}

        <TextField
          label={mode === 'adopt' ? 'Display name (optional)' : 'Portfolio name'}
          size="small" fullWidth placeholder="e.g. Smith Family SMSF"
          value={name} onChange={(e) => setName(e.target.value)}
        />
        <TextField select label="Type" size="small" fullWidth
          value={entityType} onChange={(e) => setEntityType(e.target.value)}>
          {ENTITY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>

        {mode === 'create' && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              A brand-new set of books: its own broker accounts, trades, NAV
              and unit register — completely separate from your other
              portfolios. Switch between them any time from the menu.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || busy}>
          {mode === 'create' ? 'Create portfolio' : 'Link account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntityDialog;
